import React, { useState, useEffect, useRef } from 'react'
import { toast } from 'react-toastify'
import * as Sentry from '@sentry/react'

const { logger } = Sentry;
import { updateUser, getJob } from '../../services/api'
import { toTitleCase } from '../../utils/textFormatting'
import './InterviewPreparationModal.scss'

const INTERVIEW_OVERVIEW = `
# Interview Preparation Overview

## Understanding the Interview Process

The interview process typically consists of several stages, each designed to assess different aspects of your candidacy. Understanding these stages will help you prepare effectively and navigate the process with confidence.

## Common Interview Stages

### 1. Phone/Initial Screening
This is usually the first step, where a recruiter or HR representative conducts a brief conversation to verify your qualifications and interest in the role. Prepare by:
- Researching the company and role
- Preparing a brief introduction about yourself
- Having questions ready about the position

### 2. Technical Assessment
Many technical roles include coding challenges, technical tests, or take-home assignments. To prepare:
- Review fundamental concepts relevant to the role
- Practice coding problems if applicable
- Understand the technologies mentioned in the job description

### 3. Behavioral Interview
This stage focuses on your past experiences and how you handle various situations. Use the STAR method (Situation, Task, Action, Result) to structure your responses:
- **Situation**: Set the context
- **Task**: Describe what you needed to accomplish
- **Action**: Explain what you did
- **Result**: Share the outcome

### 4. Technical Interview
For technical roles, you'll discuss your technical skills in depth. Be ready to:
- Explain your past projects in detail
- Discuss technical decisions and trade-offs
- Solve problems on the spot or discuss approaches

### 5. Panel Interview
You may meet with multiple interviewers at once. Tips for success:
- Make eye contact with all panel members
- Address questions to the person who asked, but include everyone
- Take notes if helpful

### 6. Final Round / On-site
This often includes meeting with senior leadership, team members, and potential managers. Prepare by:
- Researching each interviewer on LinkedIn
- Preparing questions about team culture and growth opportunities
- Being ready to discuss your long-term career goals

## Key Preparation Strategies

### Research
- Study the company's mission, values, and recent news
- Understand the role's responsibilities and requirements
- Research the interviewers on LinkedIn
- Review the company's products or services

### Practice
- Prepare answers to common interview questions
- Practice explaining your experience using the STAR method
- Conduct mock interviews with friends or mentors
- Prepare questions to ask the interviewer

### Mindset
- Get adequate rest before the interview
- Arrive early (or log in early for virtual interviews)
- Dress appropriately for the company culture
- Stay calm and be yourself

## During the Interview

### Communication Tips
- Listen carefully to questions before responding
- Take a moment to think if needed
- Be concise but thorough in your answers
- Ask for clarification if a question is unclear

### Body Language
- Maintain good posture
- Make appropriate eye contact
- Show enthusiasm through your tone and expressions
- Use gestures naturally to emphasize points

## After the Interview

### Follow-up
- Send a thank-you email within 24 hours
- Reference specific points from your conversation
- Reiterate your interest in the role
- Be patient while waiting for a response

### Reflection
- Note what went well and what you could improve
- Document questions asked for future preparation
- Update your interview notes for similar roles

## Common Mistakes to Avoid

1. **Not researching the company** - Shows lack of interest
2. **Arriving late** - Demonstrates poor time management
3. **Speaking negatively about previous employers** - Raises red flags
4. **Not asking questions** - Suggests lack of engagement
5. **Being unprepared** - Fails to demonstrate your capabilities

## Final Tips

Remember that interviews are a two-way street. While the company is evaluating you, you're also evaluating whether this role and company are the right fit for you. Come prepared, be authentic, and use the opportunity to learn as much as you can about the position and organization.

Good luck with your interview preparation!
`

interface InterviewQuestion {
  id: number | string
  question: string
  solution?: string
  insights?: string
  answer?: string
  type?: 'behavioral' | 'technical'
  rank?: number
}

// Sample interview questions - fallback if no enrichment data
const SAMPLE_QUESTIONS: InterviewQuestion[] = [
  {
    id: 1,
    question: "Tell me about yourself.",
    solution: "This is your opportunity to give a concise overview of your professional background. Start with your current role, highlight 2-3 key achievements, mention relevant experience, and connect it to why you're interested in this position. Keep it to 1-2 minutes."
  },
  {
    id: 2,
    question: "Why do you want to work here?",
    solution: "Show that you've done your research. Mention specific aspects of the company that align with your values and career goals. Reference recent company news, their mission, or their products. Connect your skills and interests to what the company does."
  },
  {
    id: 3,
    question: "What are your greatest strengths?",
    solution: "Choose 2-3 strengths that are relevant to the role. Provide specific examples of how you've used these strengths in previous positions. Be honest and authentic, and avoid generic answers."
  },
  {
    id: 4,
    question: "What is your greatest weakness?",
    solution: "Choose a real weakness that you're actively working to improve. Explain how you recognized it, what steps you're taking to address it, and show progress. Avoid clichés like 'I'm a perfectionist' unless you can genuinely explain it."
  },
  {
    id: 5,
    question: "Where do you see yourself in 5 years?",
    solution: "Show ambition but also commitment. Discuss how this role fits into your career trajectory. Mention skills you want to develop and how you see yourself contributing to the company's growth. Be realistic and show you've thought about your career path."
  },
  {
    id: 6,
    question: "Why are you leaving your current job?",
    solution: "Be positive and forward-looking. Focus on what you're seeking (growth opportunities, new challenges, better alignment with your goals) rather than what you're leaving behind. Never speak negatively about your current employer."
  },
  {
    id: 7,
    question: "Tell me about a time you handled a difficult situation.",
    solution: "Use the STAR method. Choose a situation that demonstrates problem-solving, resilience, or leadership. Focus on your actions and the positive outcome. Show what you learned from the experience."
  },
  {
    id: 8,
    question: "How do you handle stress and pressure?",
    solution: "Provide specific examples of stressful situations you've managed successfully. Discuss your coping strategies (prioritization, time management, communication, taking breaks). Show that you can perform well under pressure."
  },
  {
    id: 9,
    question: "What questions do you have for me?",
    solution: "Always have questions prepared. Ask about: team dynamics, growth opportunities, challenges the team is facing, company culture, what success looks like in this role, or next steps in the process. This shows engagement and genuine interest."
  },
  {
    id: 10,
    question: "Describe a time when you had to work with a difficult team member.",
    solution: "Focus on your communication and conflict resolution skills. Explain the situation objectively, describe how you addressed it professionally, and highlight the positive outcome. Show emotional intelligence and teamwork."
  }
]

interface InterviewPreparationModalProps {
  isOpen: boolean
  onClose: () => void
  jobPosting: any
  userData: any
  getAccessTokenSilently: () => Promise<string>
}

function InterviewPreparationModal({ isOpen, onClose, jobPosting, userData, getAccessTokenSilently }: InterviewPreparationModalProps) {
  const [activeTab, setActiveTab] = useState<string>('overview')
  const [questionTypeTab, setQuestionTypeTab] = useState<'behavioral' | 'technical' | 'coding' | 'all'>('all')
  const [revealedSolutions, setRevealedSolutions] = useState<Set<number>>(new Set())
  const [completedQuestions, setCompletedQuestions] = useState<Set<number>>(new Set())
  const [isSaving, setIsSaving] = useState(false)
  const [enrichedJobPosting, setEnrichedJobPosting] = useState<any>(jobPosting)
  const [isLoadingEnrichment, setIsLoadingEnrichment] = useState(false)
  const tabContentRef = useRef<HTMLDivElement>(null)

  // Fetch full job data with enrichment when modal opens
  useEffect(() => {
    if (isOpen && jobPosting?.id) {
      // Check if we need to fetch enrichment data
      const hasEnrichment = jobPosting?.enrichment && (
        jobPosting.enrichment?.fitAnalysis ||
        jobPosting.enrichment?.interviewProcess ||
        jobPosting.enrichment?.company ||
        jobPosting.enrichment?.people ||
        jobPosting.enrichment?.interviewQuestions ||
        jobPosting.enrichment?.codingQuestions
      );

      // If no enrichment data, fetch the full job
      if (!hasEnrichment) {
        setIsLoadingEnrichment(true);
        getJob(jobPosting.id, getAccessTokenSilently)
          .then((fullJob) => {
            setEnrichedJobPosting(fullJob);
          })
          .catch((error) => {
            logger.error('Error fetching full job data for interview preparation', {
              error: error instanceof Error ? error.message : String(error),
              jobId: jobPosting.id,
            });
            // Fall back to the original jobPosting if fetch fails
            setEnrichedJobPosting(jobPosting);
          })
          .finally(() => {
            setIsLoadingEnrichment(false);
          });
      } else {
        // Already has enrichment, use it directly
        setEnrichedJobPosting(jobPosting);
      }
    } else if (isOpen) {
      // Reset to original jobPosting when modal opens without ID
      setEnrichedJobPosting(jobPosting);
    }
  }, [isOpen, jobPosting, getAccessTokenSilently])

  // Load completed questions from userData when modal opens or userData changes
  useEffect(() => {
    if (isOpen && userData?.completedQuestions) {
      setCompletedQuestions(new Set(userData.completedQuestions))
    } else if (isOpen) {
      setCompletedQuestions(new Set())
    }
  }, [isOpen, userData])

  // Reset scroll position when tab changes
  useEffect(() => {
    if (tabContentRef.current) {
      tabContentRef.current.scrollTop = 0
    }
  }, [activeTab])

  // Reset question type tab when switching to questions tab
  useEffect(() => {
    if (activeTab === 'questions') {
      // Check what question types we have available
      if (enrichedJobPosting?.enrichment?.interviewQuestions || enrichedJobPosting?.enrichment?.codingQuestions) {
        const enrichmentQuestions = enrichedJobPosting.enrichment?.interviewQuestions;
        const hasBehavioral = enrichmentQuestions?.behavioral && enrichmentQuestions.behavioral.length > 0;
        const hasTechnical = enrichmentQuestions?.technical && enrichmentQuestions.technical.length > 0;
        const hasCoding = enrichedJobPosting.enrichment?.codingQuestions && enrichedJobPosting.enrichment.codingQuestions.length > 0;
        
        // Default to first available type
        if (hasBehavioral) {
          setQuestionTypeTab('behavioral');
        } else if (hasTechnical) {
          setQuestionTypeTab('technical');
        } else if (hasCoding) {
          setQuestionTypeTab('coding');
        } else {
          setQuestionTypeTab('all');
        }
      } else {
        setQuestionTypeTab('all');
      }
    }
  }, [activeTab, enrichedJobPosting])

  if (!isOpen) return null

  // Use enriched job posting for all data access
  const currentJobPosting = enrichedJobPosting || jobPosting;

  const toggleSolution = (questionId: number) => {
    setRevealedSolutions(prev => {
      const newSet = new Set(prev)
      if (newSet.has(questionId)) {
        newSet.delete(questionId)
      } else {
        newSet.add(questionId)
      }
      return newSet
    })
  }

  const toggleQuestionCompleted = async (questionId: number | string) => {
    const wasCompleted = completedQuestions.has(questionId)
    const newCompletedQuestions = new Set(completedQuestions)
    if (wasCompleted) {
      newCompletedQuestions.delete(questionId)
    } else {
      newCompletedQuestions.add(questionId)
    }
    
    // Update local state immediately for better UX
    setCompletedQuestions(newCompletedQuestions)
    
    // Save to backend
    if (getAccessTokenSilently && currentJobPosting?.id) {
      setIsSaving(true)
      try {
        await updateUser(
          { 
            completedQuestions: Array.from(newCompletedQuestions),
            // Include jobId and questionId for activity creation
            _questionAction: wasCompleted ? 'unmarked' : 'marked',
            _jobId: currentJobPosting.id,
            _questionId: questionId
          },
          getAccessTokenSilently
        )
      } catch (error) {
        logger.error('Error saving completed questions', {
          error: error instanceof Error ? error.message : String(error),
        });
        // Revert on error
        setCompletedQuestions(completedQuestions)
        toast.error('Failed to save question status. Please try again.')
      } finally {
        setIsSaving(false)
      }
    }
  }

  return (
    <div className="modal-overlay">
      <div className="interview-modal-content">
        <div className="interview-modal-header">
          <h3 className="interview-modal-title">
            <span>Interview Preparation - {toTitleCase(currentJobPosting?.title) || 'Job Position'}</span>
            {isLoadingEnrichment && (
              <span className="ms-2" style={{ fontSize: '0.8em', color: '#666' }}>
                Loading enrichment data...
              </span>
            )}
          </h3>
          <button
            className="interview-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="interview-modal-body">
          {/* Vertical Tabs */}
          <div className="interview-tabs">
            <button
              className={`interview-tab ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              <span className="tab-icon">📊</span>
              <span>Overview</span>
            </button>
            <button
              className={`interview-tab ${activeTab === 'process' ? 'active' : ''}`}
              onClick={() => setActiveTab('process')}
            >
              <span className="tab-icon">🔄</span>
              <span>Process</span>
            </button>
            <button
              className={`interview-tab ${activeTab === 'company' ? 'active' : ''}`}
              onClick={() => setActiveTab('company')}
            >
              <span className="tab-icon">🏢</span>
              <span>Company</span>
            </button>
            <button
              className={`interview-tab ${activeTab === 'people' ? 'active' : ''}`}
              onClick={() => setActiveTab('people')}
            >
              <span className="tab-icon">👥</span>
              <span>People</span>
            </button>
            <button
              className={`interview-tab ${activeTab === 'questions' ? 'active' : ''}`}
              onClick={() => setActiveTab('questions')}
            >
              <span className="tab-icon">❓</span>
              <span>Question Bank</span>
            </button>
          </div>

          {/* Tab Content */}
          <div className="interview-tab-content" ref={tabContentRef}>
            {activeTab === 'overview' && (
              <div className="overview-content">
                {/* Show loading state while fetching enrichment */}
                {isLoadingEnrichment ? (
                  <div className="company-enrichment-loading">
                    <p>Loading job fit analysis...</p>
                  </div>
                ) : (
                  <>
                    {/* Candidate-Job Fit Analysis */}
                    {currentJobPosting?.enrichment?.fitAnalysis ? (
                  <div className="fit-analysis-section">
                    {/* Score Visualization */}
                    <div className="fit-score-card">
                      <h3 className="fit-score-title">Job Fit Score</h3>
                      <div className="fit-score-visualization">
                        <div className="fit-score-circle">
                          <svg className="fit-score-svg" viewBox="0 0 120 120">
                            <circle
                              className="fit-score-bg"
                              cx="60"
                              cy="60"
                              r="54"
                              fill="none"
                              stroke="#e0e0e0"
                              strokeWidth="12"
                            />
                            <circle
                              className="fit-score-progress"
                              cx="60"
                              cy="60"
                              r="54"
                              fill="none"
                              stroke={currentJobPosting.enrichment.fitAnalysis.score >= 70 ? '#4caf50' : currentJobPosting.enrichment.fitAnalysis.score >= 50 ? '#ff9800' : '#f44336'}
                              strokeWidth="12"
                              strokeLinecap="round"
                              strokeDasharray={`${2 * Math.PI * 54}`}
                              strokeDashoffset={`${2 * Math.PI * 54 * (1 - currentJobPosting.enrichment.fitAnalysis.score / 100)}`}
                              transform="rotate(-90 60 60)"
                            />
                          </svg>
                          <div className="fit-score-value">
                            <span className="fit-score-number">{currentJobPosting.enrichment.fitAnalysis.score}</span>
                            <span className="fit-score-label">/ 100</span>
                          </div>
                        </div>
                        <div className="fit-score-description">
                          {currentJobPosting.enrichment.fitAnalysis.score >= 80 && (
                            <p className="fit-score-text excellent">Excellent Match</p>
                          )}
                          {currentJobPosting.enrichment.fitAnalysis.score >= 60 && currentJobPosting.enrichment.fitAnalysis.score < 80 && (
                            <p className="fit-score-text good">Good Match</p>
                          )}
                          {currentJobPosting.enrichment.fitAnalysis.score >= 40 && currentJobPosting.enrichment.fitAnalysis.score < 60 && (
                            <p className="fit-score-text moderate">Moderate Match</p>
                          )}
                          {currentJobPosting.enrichment.fitAnalysis.score < 40 && (
                            <p className="fit-score-text poor">Needs Improvement</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Summary */}
                    {currentJobPosting.enrichment.fitAnalysis.summary && (
                      <div className="fit-analysis-card">
                        <h4 className="fit-analysis-heading">Summary</h4>
                        <div 
                          className="fit-analysis-content"
                          dangerouslySetInnerHTML={{ __html: currentJobPosting.enrichment.fitAnalysis.summary }}
                        />
                      </div>
                    )}

                    {/* Strengths */}
                    {currentJobPosting.enrichment.fitAnalysis.strengths && (
                      <div className="fit-analysis-card fit-analysis-strengths">
                        <h4 className="fit-analysis-heading">Strengths</h4>
                        <div 
                          className="fit-analysis-content"
                          dangerouslySetInnerHTML={{ __html: currentJobPosting.enrichment.fitAnalysis.strengths }}
                        />
                      </div>
                    )}

                    {/* Gaps */}
                    {currentJobPosting.enrichment.fitAnalysis.gaps && (
                      <div className="fit-analysis-card fit-analysis-gaps">
                        <h4 className="fit-analysis-heading">Gaps</h4>
                        <div 
                          className="fit-analysis-content"
                          dangerouslySetInnerHTML={{ __html: currentJobPosting.enrichment.fitAnalysis.gaps }}
                        />
                      </div>
                    )}

                    {/* Insights */}
                    {currentJobPosting.enrichment.fitAnalysis.insights && (
                      <div className="fit-analysis-card fit-analysis-insights">
                        <h4 className="fit-analysis-heading">Insights & Recommendations</h4>
                        <div 
                          className="fit-analysis-content"
                          dangerouslySetInnerHTML={{ __html: currentJobPosting.enrichment.fitAnalysis.insights }}
                        />
                      </div>
                    )}

                    {/* Preparation Actions */}
                    {currentJobPosting.enrichment.fitAnalysis.preparationActions && (
                      <div className="fit-analysis-card fit-analysis-preparation">
                        <h4 className="fit-analysis-heading">Interview Preparation Actions</h4>
                        <div 
                          className="fit-analysis-content"
                          dangerouslySetInnerHTML={{ __html: currentJobPosting.enrichment.fitAnalysis.preparationActions }}
                        />
                      </div>
                    )}
                  </div>
                    ) : currentJobPosting?.state === 'imported' ? (
                      <div className="company-enrichment-loading">
                        <p>Fit analysis is being generated. Please check back in a few moments.</p>
                      </div>
                    ) : (
                      <div className="company-enrichment-empty">
                        <p>We don't have fit analysis available at this time.</p>
                      </div>
                    )}

                    {/* Disclaimer */}
                    <div className="fit-analysis-disclaimer">
                      <p className="disclaimer-text">
                        <small>
                          <strong>Disclaimer:</strong> The information provided is sourced from anonymous data and we cannot guarantee its accuracy. This analysis is for informational purposes only and should not be the sole basis for career decisions.
                        </small>
                      </p>
                    </div>
                  </>
                )}
              </div>
            )}

            {activeTab === 'process' && (
              <div className="process-content">
                {/* Show loading state while fetching enrichment */}
                {isLoadingEnrichment ? (
                  <div className="company-enrichment-loading">
                    <p>Loading interview process information...</p>
                  </div>
                ) : (
                  <>
                    {/* Enriched Interview Process Data */}
                    {currentJobPosting?.enrichment?.interviewProcess?.overview ? (
                      <div className="company-enrichment-section">
                        <div className="company-enrichment-card">
                          {/* Gemini-generated interview process overview */}
                          <div 
                            className="company-overview"
                            dangerouslySetInnerHTML={{ __html: currentJobPosting.enrichment.interviewProcess.overview }}
                          />
                          <div className="company-ai-disclaimer">
                            <small>⚠️ This information is gathered from anonymous sources and we do not guarantee its correctness. Please verify important details independently.</small>
                          </div>
                        </div>
                      </div>
                    ) : currentJobPosting?.state === 'imported' ? (
                      <div className="company-enrichment-loading">
                        <p>Interview process information is being enriched. Please check back in a few moments.</p>
                      </div>
                    ) : (
                      <div className="company-enrichment-empty">
                        <p>We don't have interview process information available at this time.</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {activeTab === 'company' && (
              <div className="company-content">
                {/* Show loading state while fetching enrichment */}
                {isLoadingEnrichment ? (
                  <div className="company-enrichment-loading">
                    <p>Loading company information...</p>
                  </div>
                ) : (
                  <>
                    {/* Enriched Company Data */}
                    {currentJobPosting?.enrichment?.company?.overview ? (
                      <div className="company-enrichment-section">
                        <div className="company-enrichment-card">
                          {/* Gemini AI-generated interview process overview */}
                          <div 
                            className="company-overview"
                            dangerouslySetInnerHTML={{ __html: currentJobPosting.enrichment.company.overview }}
                          />
                          <div className="company-ai-disclaimer">
                            <small>⚠️ This information is gathered from anonymous sources and we do not guarantee its correctness. Please verify important details independently.</small>
                          </div>
                        </div>
                      </div>
                    ) : currentJobPosting?.state === 'imported' ? (
                      <div className="company-enrichment-loading">
                        <p>Company information is being enriched. Please check back in a few moments.</p>
                      </div>
                    ) : (
                      <div className="company-enrichment-empty">
                        <p>We don't have company information available at this time.</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {activeTab === 'people' && (
              <div className="people-content">
                {/* Show loading state while fetching enrichment */}
                {isLoadingEnrichment ? (
                  <div className="people-loading-state">
                    <p>Loading people information...</p>
                  </div>
                ) : (
                  <>
                    <div className="people-header">
                      <h4>People at {currentJobPosting?.company || 'Company'}</h4>
                      <p className="people-subtitle">
                        Connect with professionals who hold similar roles at {currentJobPosting?.company || 'this company'}.
                      </p>
                    </div>

                    {/* Enriched People Data */}
                    {currentJobPosting?.enrichment?.people && (
                  <div className="people-grid">
                    {(() => {
                      // Extract people from the API response
                      // People Data Labs returns data in different formats, handle both
                      let peopleList: any[] = [];
                      const peopleData = currentJobPosting.enrichment.people;
                      
                      // Handle different response structures
                      // People Data Labs API returns: { total: number, data: [...], status: number }
                      if (Array.isArray(peopleData)) {
                        // If it's already an array, use it directly
                        peopleList = peopleData;
                      } else if (peopleData && typeof peopleData === 'object') {
                        // Check for data array (most common format)
                        if (peopleData.data) {
                          if (Array.isArray(peopleData.data)) {
                            peopleList = peopleData.data;
                          } else if (peopleData.data?.L && Array.isArray(peopleData.data.L)) {
                            // Handle DynamoDB List format (if somehow preserved)
                            peopleList = peopleData.data.L;
                          }
                        } else if (peopleData.people && Array.isArray(peopleData.people)) {
                          // Alternative format
                          peopleList = peopleData.people;
                        } else if (Array.isArray(peopleData.results)) {
                          // Another possible format
                          peopleList = peopleData.results;
                        }
                      }

                      if (peopleList.length === 0) {
                        return (
                          <div className="people-empty-state">
                            <p>No people found for this role at this company.</p>
                          </div>
                        );
                      }

                      return peopleList.map((person: any, index: number) => {
                        const fullName = person.full_name || person.name || `${person.first_name || ''} ${person.last_name || ''}`.trim() || 'Unknown';
                        const title = toTitleCase(person.job_title || person.title || currentJobPosting?.title || 'Unknown Title');
                        const company = person.job_company_name || person.company_name || currentJobPosting?.company || 'Unknown Company';
                        // Use location_name, location, or location_country (in that order)
                        const location = person.location_name || person.location || person.location_country || currentJobPosting?.location || '';
                        
                        // Extract URLs from profiles array or direct fields
                        const profiles = person.profiles || [];
                        const linkedinProfile = profiles.find((p: any) => p.network === 'linkedin');
                        const facebookProfile = profiles.find((p: any) => p.network === 'facebook');
                        const githubProfile = profiles.find((p: any) => p.network === 'github');
                        
                        // Get URLs, adding https:// if needed
                        const normalizeUrl = (url: string | null | undefined): string | null => {
                          if (!url) return null;
                          const urlStr = url.toString().trim();
                          if (!urlStr) return null;
                          if (urlStr.startsWith('http://') || urlStr.startsWith('https://')) {
                            return urlStr;
                          }
                          return `https://${urlStr}`;
                        };
                        
                        // For Google Custom Search results, use profile_url as LinkedIn URL
                        // For People Data Labs, use linkedin_url or profile URL
                        const linkedinUrl = normalizeUrl(
                          person.profile_url || person.linkedin_url || linkedinProfile?.url
                        );
                        const facebookUrl = normalizeUrl(
                          person.facebook_url || facebookProfile?.url
                        );
                        const githubUrl = normalizeUrl(
                          person.github_url || githubProfile?.url
                        );
                        
                        // Extract image URL - prioritize profile_picture_url, then image_url
                        const imageUrl = person.profile_picture_url || person.image_url || null;
                        
                        return (
                          <div key={person.id || index} className="people-card">
                            <div className="people-card-header">
                              <div className="people-avatar">
                                {imageUrl ? (
                                  <img src={imageUrl} alt={fullName} />
                                ) : (
                                  <div className="people-avatar-placeholder">
                                    {fullName.split(' ').map((n: string) => n[0]).join('').toUpperCase().substring(0, 2)}
                                  </div>
                                )}
                              </div>
                              <div className="people-info">
                                <h5 className="people-name">{fullName}</h5>
                                <p className="people-title">{title}</p>
                                <p className="people-company">{company}</p>
                                {location && (
                                  <p className="people-location">📍 {location}</p>
                                )}
                              </div>
                            </div>
                            {(linkedinUrl || facebookUrl || githubUrl) && (
                              <div className="people-card-footer">
                                <div className="people-social-links">
                                  {linkedinUrl && (
                                    <a
                                      href={linkedinUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="people-social-link"
                                      title="LinkedIn Profile"
                                    >
                                      <svg
                                        width="20"
                                        height="20"
                                        viewBox="0 0 24 24"
                                        fill="currentColor"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                                      </svg>
                                    </a>
                                  )}
                                  {facebookUrl && (
                                    <a
                                      href={facebookUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="people-social-link"
                                      title="Facebook Profile"
                                    >
                                      <svg
                                        width="20"
                                        height="20"
                                        viewBox="0 0 24 24"
                                        fill="currentColor"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                                      </svg>
                                    </a>
                                  )}
                                  {githubUrl && (
                                    <a
                                      href={githubUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="people-social-link"
                                      title="GitHub Profile"
                                    >
                                      <svg
                                        width="20"
                                        height="20"
                                        viewBox="0 0 24 24"
                                        fill="currentColor"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                                      </svg>
                                    </a>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      });
                    })()}
                  </div>
                )}

                {!currentJobPosting?.enrichment?.people && currentJobPosting?.state === 'imported' && (
                  <div className="people-loading-state">
                    <p>People information is being enriched. Please check back in a few moments.</p>
                  </div>
                )}

                    {!currentJobPosting?.enrichment?.people && currentJobPosting?.state !== 'imported' && (
                      <div className="people-empty-state">
                        <p>No people data available for this job posting.</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {activeTab === 'questions' && (
              <div className="questions-content">
                {/* Show loading state while fetching enrichment */}
                {isLoadingEnrichment ? (
                  <div className="company-enrichment-loading">
                    <p>Loading interview questions...</p>
                  </div>
                ) : (
                  <>
                    <div className="questions-header">
                      <h4>Interview Question Bank</h4>
                      <p className="questions-subtitle">
                        Practice with interview questions tailored to this position. Click "Show Suggestion" to reveal insights and suggested answers.
                      </p>
                    </div>
                    {(() => {
                  // Get questions from enrichment data or fallback to sample questions
                  let questions: InterviewQuestion[] = [];
                  
                  if (currentJobPosting?.enrichment?.interviewQuestions) {
                    const enrichmentQuestions = currentJobPosting.enrichment.interviewQuestions;
                    
                    // Combine behavioral and technical questions
                    const behavioral = (enrichmentQuestions.behavioral || []).map((q: any, index: number) => ({
                      id: `behavioral-${q.rank || index + 1}`,
                      question: q.question,
                      insights: q.insights,
                      answer: q.answer,
                      type: 'behavioral' as const,
                      rank: q.rank || index + 1
                    }));
                    const technical = (enrichmentQuestions.technical || []).map((q: any, index: number) => ({
                      id: `technical-${q.rank || index + 1}`,
                      question: q.question,
                      insights: q.insights,
                      answer: q.answer,
                      type: 'technical' as const,
                      rank: q.rank || index + 1
                    }));
                    questions = [...behavioral, ...technical];
                    
                    // If enrichment questions exist but are empty, fall back to sample questions
                    if (questions.length === 0) {
                      questions = SAMPLE_QUESTIONS;
                    }
                  } else {
                    // Fallback to sample questions
                    questions = SAMPLE_QUESTIONS;
                  }

                  // Only show loading/empty state if job is still being enriched AND we have no questions at all
                  if (questions.length === 0 && currentJobPosting?.state === 'imported') {
                    return (
                      <div className="company-enrichment-loading">
                        <p>Interview questions are being generated. Please check back in a few moments.</p>
                      </div>
                    );
                  }

                  // Group questions by type if we have enrichment data
                  // Sort each group: completed questions at the bottom
                  const sortQuestions = (qList: InterviewQuestion[]) => {
                    return [...qList].sort((a, b) => {
                      const aCompleted = completedQuestions.has(a.id);
                      const bCompleted = completedQuestions.has(b.id);
                      // If one is completed and the other isn't, completed goes to bottom
                      if (aCompleted && !bCompleted) return 1;
                      if (!aCompleted && bCompleted) return -1;
                      // If both have same completion status, maintain original order (by rank if available)
                      const aRank = a.rank || 0;
                      const bRank = b.rank || 0;
                      return aRank - bRank;
                    });
                  };
                  
                  const behavioralQuestions = sortQuestions(questions.filter(q => q.type === 'behavioral'));
                  const technicalQuestions = sortQuestions(questions.filter(q => q.type === 'technical'));
                  const otherQuestions = sortQuestions(questions.filter(q => !q.type));

                  // Determine which questions to show based on active tab
                  const hasBehavioral = behavioralQuestions.length > 0;
                  const hasTechnical = technicalQuestions.length > 0;
                  const hasCoding = currentJobPosting?.enrichment?.codingQuestions && currentJobPosting.enrichment.codingQuestions.length > 0;
                  
                  // Show tabs if we have multiple question types
                  const showTabs = (hasBehavioral && hasTechnical) || (hasBehavioral && hasCoding) || (hasTechnical && hasCoding) || (hasBehavioral && hasTechnical && hasCoding);

                  // Get questions to display based on active tab
                  let questionsToDisplay: InterviewQuestion[] = [];
                  if (questionTypeTab === 'behavioral') {
                    questionsToDisplay = behavioralQuestions;
                  } else if (questionTypeTab === 'technical') {
                    questionsToDisplay = technicalQuestions;
                  } else if (questionTypeTab === 'coding') {
                    // Coding questions will be handled separately in the coding section
                    questionsToDisplay = [];
                  } else {
                    // 'all' - show all questions, but group them (used when tabs are not shown)
                    questionsToDisplay = [...behavioralQuestions, ...technicalQuestions, ...otherQuestions];
                  }

                  return (
                    <>
                      {showTabs && (
                        <div className="question-type-tabs">
                          {hasBehavioral && (
                            <button
                              className={`question-type-tab ${questionTypeTab === 'behavioral' ? 'active' : ''}`}
                              onClick={() => setQuestionTypeTab('behavioral')}
                            >
                              Behavioral
                              {behavioralQuestions.length > 0 && (
                                <span className="tab-count">({behavioralQuestions.length})</span>
                              )}
                            </button>
                          )}
                          {hasTechnical && (
                            <button
                              className={`question-type-tab ${questionTypeTab === 'technical' ? 'active' : ''}`}
                              onClick={() => setQuestionTypeTab('technical')}
                            >
                              Technical
                              {technicalQuestions.length > 0 && (
                                <span className="tab-count">({technicalQuestions.length})</span>
                              )}
                            </button>
                          )}
                          {hasCoding && (
                            <button
                              className={`question-type-tab ${questionTypeTab === 'coding' ? 'active' : ''}`}
                              onClick={() => setQuestionTypeTab('coding')}
                            >
                              Coding
                              <span className="tab-count">({currentJobPosting?.enrichment?.codingQuestions?.length || 0})</span>
                            </button>
                          )}
                        </div>
                      )}
                      <div className="flash-cards-container">
                        {questionTypeTab === 'coding' ? (
                          // Coding questions section
                          <div className="coding-questions-list">
                            {currentJobPosting?.enrichment?.codingQuestions && currentJobPosting.enrichment.codingQuestions.length > 0 ? (
                              currentJobPosting.enrichment.codingQuestions.map((q: any, index: number) => {
                              const questionId = `coding-${q.rank || index + 1}`;
                              const isRevealed = revealedSolutions.has(questionId);
                              const isCompleted = completedQuestions.has(questionId);
                              
                              // Get difficulty color
                              const getDifficultyColor = (difficulty: string) => {
                                switch (difficulty?.toLowerCase()) {
                                  case 'easy':
                                    return '#4caf50';
                                  case 'hard':
                                    return '#f44336';
                                  default:
                                    return '#ff9800';
                                }
                              };

                              return (
                                <div key={questionId} className={`coding-question-card ${isCompleted ? 'completed' : ''}`}>
                                  <div className="coding-question-header">
                                    <div className="coding-question-title-section">
                                      <h5 className="coding-question-title">
                                        <span className="question-rank">#{q.rank || index + 1}</span>
                                        <span 
                                          className="question-text"
                                          dangerouslySetInnerHTML={{ __html: q.question }}
                                        />
                                      </h5>
                                      {q.difficulty && (
                                        <span 
                                          className="difficulty-badge"
                                          style={{ 
                                            backgroundColor: getDifficultyColor(q.difficulty),
                                            color: 'white'
                                          }}
                                        >
                                          {q.difficulty}
                                        </span>
                                      )}
                                    </div>
                                  </div>

                                  <div className="coding-question-actions">
                                    <button
                                      className="btn btn-outline-primary btn-sm show-solution-btn"
                                      onClick={() => toggleSolution(questionId)}
                                    >
                                      {isRevealed ? 'Hide Solution' : 'Show Solution'}
                                    </button>

                                    <button
                                      className={`btn btn-sm mark-done-btn ${isCompleted ? 'btn-success' : 'btn-outline-secondary'}`}
                                      onClick={() => toggleQuestionCompleted(questionId)}
                                      disabled={isSaving}
                                      title={isCompleted ? 'Mark as not done' : 'Mark as done'}
                                    >
                                      {isCompleted ? '✓ Done' : 'Mark As Done'}
                                    </button>
                                  </div>

                                  {isRevealed && (
                                    <div className="coding-question-solution">
                                      {q.solution && (
                                        <div className="solution-section">
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                                            <strong>Solution:</strong>
                                            {q.language && (
                                              <span className="language-badge">{q.language}</span>
                                            )}
                                          </div>
                                          <pre className="code-block">
                                            <code>{q.solution}</code>
                                          </pre>
                                        </div>
                                      )}
                                      {q.explanation && (
                                        <div className="solution-section">
                                          <strong>Explanation:</strong>
                                          <div 
                                            className="solution-content"
                                            dangerouslySetInnerHTML={{ __html: q.explanation }}
                                          />
                                        </div>
                                      )}
                                      {q.insights && (
                                        <div className="solution-section">
                                          <strong>What the Interviewer is Looking For:</strong>
                                          <div 
                                            className="solution-content"
                                            dangerouslySetInnerHTML={{ __html: q.insights }}
                                          />
                                        </div>
                                      )}
                                      {q.gotchas && (
                                        <div className="solution-section">
                                          <strong>Potential Gotchas & Things to Watch For:</strong>
                                          <div 
                                            className="solution-content"
                                            dangerouslySetInnerHTML={{ __html: q.gotchas }}
                                          />
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })
                            ) : (
                              <div className="company-enrichment-empty">
                                <p>No coding questions available.</p>
                              </div>
                            )}
                          </div>
                        ) : showTabs ? (
                          // When tabs are shown, display filtered questions without section titles
                          <div className="questions-section">
                            {questionsToDisplay.map((item) => (
                              <div key={item.id} className={`flash-card ${completedQuestions.has(item.id) ? 'completed' : ''}`}>
                                <div className="flash-card-header">
                                  <div className="flash-card-question">
                                    <h5>{item.question}</h5>
                                  </div>
                                </div>
                                <div className="coding-question-actions">
                                  <button
                                    className="btn btn-outline-primary btn-sm show-solution-btn"
                                    onClick={() => toggleSolution(item.id)}
                                  >
                                    {revealedSolutions.has(item.id) ? 'Hide Suggestion' : 'Show Suggestion'}
                                  </button>
                                  <button
                                    className={`btn btn-sm mark-done-btn ${completedQuestions.has(item.id) ? 'btn-success' : 'btn-outline-secondary'}`}
                                    onClick={() => toggleQuestionCompleted(item.id)}
                                    disabled={isSaving}
                                    title={completedQuestions.has(item.id) ? 'Mark as not done' : 'Mark as done'}
                                  >
                                    {completedQuestions.has(item.id) ? '✓ Done' : 'Mark As Done'}
                                  </button>
                                </div>
                                {revealedSolutions.has(item.id) && (
                                  <div className="flash-card-solution">
                                    {item.insights && (
                                      <div className="solution-section">
                                        <strong>What the Interviewer is Looking For:</strong>
                                        <div 
                                          className="solution-content"
                                          dangerouslySetInnerHTML={{ __html: item.insights }}
                                        />
                                      </div>
                                    )}
                                    {item.answer && (
                                      <div className="solution-section">
                                        <strong>Example Answer:</strong>
                                        <div 
                                          className="solution-content"
                                          dangerouslySetInnerHTML={{ __html: item.answer }}
                                        />
                                      </div>
                                    )}
                                    {item.solution && !item.answer && (
                                      <div className="solution-section">
                                        <strong>Suggested Answer:</strong>
                                        <p>{item.solution}</p>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          // When tabs are not shown, display grouped sections with titles
                          <>
                            {behavioralQuestions.length > 0 && (
                              <div className="questions-section">
                                <h5 className="questions-section-title">Behavioral Questions</h5>
                                {behavioralQuestions.map((item) => (
                            <div key={item.id} className={`flash-card ${completedQuestions.has(item.id) ? 'completed' : ''}`}>
                              <div className="flash-card-header">
                                <div className="flash-card-question">
                                  <h5>{item.question}</h5>
                                </div>
                              </div>
                              <div className="coding-question-actions">
                                <button
                                  className="btn btn-outline-primary btn-sm show-solution-btn"
                                  onClick={() => toggleSolution(item.id)}
                                >
                                  {revealedSolutions.has(item.id) ? 'Hide Suggestion' : 'Show Suggestion'}
                                </button>
                                <button
                                  className={`btn btn-sm mark-done-btn ${completedQuestions.has(item.id) ? 'btn-success' : 'btn-outline-secondary'}`}
                                  onClick={() => toggleQuestionCompleted(item.id)}
                                  disabled={isSaving}
                                  title={completedQuestions.has(item.id) ? 'Mark as not done' : 'Mark as done'}
                                >
                                  {completedQuestions.has(item.id) ? '✓ Done' : 'Mark As Done'}
                                </button>
                              </div>
                              {revealedSolutions.has(item.id) && (
                                <div className="flash-card-solution">
                                  {item.insights && (
                                    <div className="solution-section">
                                      <strong>What the Interviewer is Looking For:</strong>
                                      <div 
                                        className="solution-content"
                                        dangerouslySetInnerHTML={{ __html: item.insights }}
                                      />
                                    </div>
                                  )}
                                  {item.answer && (
                                    <div className="solution-section">
                                      <strong>Example Answer:</strong>
                                      <div 
                                        className="solution-content"
                                        dangerouslySetInnerHTML={{ __html: item.answer }}
                                      />
                                    </div>
                                  )}
                                  {item.solution && !item.answer && (
                                    <div className="solution-section">
                                      <strong>Suggested Answer:</strong>
                                      <p>{item.solution}</p>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                              </div>
                            )}
                            {technicalQuestions.length > 0 && (
                              <div className="questions-section">
                                <h5 className="questions-section-title">Technical Questions</h5>
                                {technicalQuestions.map((item) => (
                            <div key={item.id} className={`flash-card ${completedQuestions.has(item.id) ? 'completed' : ''}`}>
                              <div className="flash-card-header">
                                <div className="flash-card-question">
                                  <h5>{item.question}</h5>
                                </div>
                              </div>
                              <div className="coding-question-actions">
                                <button
                                  className="btn btn-outline-primary btn-sm show-solution-btn"
                                  onClick={() => toggleSolution(item.id)}
                                >
                                  {revealedSolutions.has(item.id) ? 'Hide Suggestion' : 'Show Suggestion'}
                                </button>
                                <button
                                  className={`btn btn-sm mark-done-btn ${completedQuestions.has(item.id) ? 'btn-success' : 'btn-outline-secondary'}`}
                                  onClick={() => toggleQuestionCompleted(item.id)}
                                  disabled={isSaving}
                                  title={completedQuestions.has(item.id) ? 'Mark as not done' : 'Mark as done'}
                                >
                                  {completedQuestions.has(item.id) ? '✓ Done' : 'Mark As Done'}
                                </button>
                              </div>
                              {revealedSolutions.has(item.id) && (
                                <div className="flash-card-solution">
                                  {item.insights && (
                                    <div className="solution-section">
                                      <strong>What the Interviewer is Looking For:</strong>
                                      <div 
                                        className="solution-content"
                                        dangerouslySetInnerHTML={{ __html: item.insights }}
                                      />
                                    </div>
                                  )}
                                  {item.answer && (
                                    <div className="solution-section">
                                      <strong>Example Answer:</strong>
                                      <div 
                                        className="solution-content"
                                        dangerouslySetInnerHTML={{ __html: item.answer }}
                                      />
                                    </div>
                                  )}
                                  {item.solution && !item.answer && (
                                    <div className="solution-section">
                                      <strong>Suggested Answer:</strong>
                                      <p>{item.solution}</p>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                                ))}
                              </div>
                            )}
                            {otherQuestions.length > 0 && (
                              <div className="questions-section">
                                {otherQuestions.map((item) => (
                                  <div key={item.id} className={`flash-card ${completedQuestions.has(item.id) ? 'completed' : ''}`}>
                                    <div className="flash-card-header">
                                      <div className="flash-card-question">
                                        <h5>{item.question}</h5>
                                      </div>
                                      <button
                                        className={`btn btn-sm mark-done-btn ${completedQuestions.has(item.id) ? 'btn-success' : 'btn-outline-secondary'}`}
                                        onClick={() => toggleQuestionCompleted(item.id)}
                                        disabled={isSaving}
                                        title={completedQuestions.has(item.id) ? 'Mark as not done' : 'Mark as done'}
                                      >
                                        {completedQuestions.has(item.id) ? '✓ Done' : 'Mark As Done'}
                                      </button>
                                    </div>
                                    <button
                                      className="btn btn-outline-primary btn-sm show-solution-btn"
                                      onClick={() => toggleSolution(item.id)}
                                    >
                                      {revealedSolutions.has(item.id) ? 'Hide Suggestion' : 'Show Suggestion'}
                                    </button>
                                    {revealedSolutions.has(item.id) && (
                                      <div className="flash-card-solution">
                                        <strong>Suggested Answer:</strong>
                                        <p>{item.solution}</p>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                            {hasCoding && currentJobPosting?.enrichment?.codingQuestions && (
                              <div className="questions-section">
                                <h5 className="questions-section-title">Coding Questions</h5>
                                {currentJobPosting?.enrichment?.codingQuestions && currentJobPosting.enrichment.codingQuestions.length > 0 ? (
                                  currentJobPosting.enrichment.codingQuestions.map((q: any, index: number) => {
                                  const questionId = `coding-${q.rank || index + 1}`;
                                  const isRevealed = revealedSolutions.has(questionId);
                                  const isCompleted = completedQuestions.has(questionId);
                                  
                                  const getDifficultyColor = (difficulty: string) => {
                                    switch (difficulty?.toLowerCase()) {
                                      case 'easy':
                                        return '#4caf50';
                                      case 'hard':
                                        return '#f44336';
                                      default:
                                        return '#ff9800';
                                    }
                                  };

                                  return (
                                    <div key={questionId} className={`coding-question-card ${isCompleted ? 'completed' : ''}`}>
                                      <div className="coding-question-header">
                                        <div className="coding-question-title-section">
                                          <h5 className="coding-question-title">
                                            <span className="question-rank">#{q.rank || index + 1}</span>
                                            <span 
                                              dangerouslySetInnerHTML={{ __html: q.question }}
                                            />
                                          </h5>
                                          {q.difficulty && (
                                            <span 
                                              className="difficulty-badge"
                                              style={{ 
                                                backgroundColor: getDifficultyColor(q.difficulty),
                                                color: 'white'
                                              }}
                                            >
                                              {q.difficulty}
                                            </span>
                                          )}
                                        </div>
                                      </div>

                                      <div className="coding-question-actions">
                                        <button
                                          className="btn btn-outline-primary btn-sm show-solution-btn"
                                          onClick={() => toggleSolution(questionId)}
                                        >
                                          {isRevealed ? 'Hide Solution' : 'Show Solution'}
                                        </button>

                                        <button
                                          className={`btn btn-sm mark-done-btn ${isCompleted ? 'btn-success' : 'btn-outline-secondary'}`}
                                          onClick={() => toggleQuestionCompleted(questionId)}
                                          disabled={isSaving}
                                          title={isCompleted ? 'Mark as not done' : 'Mark as done'}
                                        >
                                          {isCompleted ? '✓ Done' : 'Mark As Done'}
                                        </button>
                                      </div>

                                      {isRevealed && (
                                        <div className="coding-question-solution">
                                          {q.solution && (
                                            <div className="solution-section">
                                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                                                <strong>Solution:</strong>
                                                {q.language && (
                                                  <span className="language-badge">{q.language}</span>
                                                )}
                                              </div>
                                              <pre className="code-block">
                                                <code>{q.solution}</code>
                                              </pre>
                                            </div>
                                          )}
                                          {q.explanation && (
                                            <div className="solution-section">
                                              <strong>Explanation:</strong>
                                              <div 
                                                className="solution-content"
                                                dangerouslySetInnerHTML={{ __html: q.explanation }}
                                              />
                                            </div>
                                          )}
                                          {q.insights && (
                                            <div className="solution-section">
                                              <strong>What the Interviewer is Looking For:</strong>
                                              <div 
                                                className="solution-content"
                                                dangerouslySetInnerHTML={{ __html: q.insights }}
                                              />
                                            </div>
                                          )}
                                          {q.gotchas && (
                                            <div className="solution-section">
                                              <strong>Potential Gotchas & Things to Watch For:</strong>
                                              <div 
                                                className="solution-content"
                                                dangerouslySetInnerHTML={{ __html: q.gotchas }}
                                              />
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })
                                ) : (
                                  <div className="company-enrichment-empty">
                                    <p>No coding questions available.</p>
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </>
                  );
                })()}
                  </>
                )}
              </div>
            )}

          </div>
        </div>

        <div className="interview-modal-footer">
          <button
            type="button"
            className="btn btn-primary"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export default InterviewPreparationModal
